/* eslint-disable import/max-dependencies */
import { statSync } from "fs"
import { fileWrite } from "@dmail/helper"
import { ressourceToContentType, defaultContentTypeMap } from "@dmail/server"
import { hrefToPathname } from "@jsenv/href"
import { normalizeImportMap, composeTwoImportMaps, resolveImport } from "@jsenv/import-map"
import { generateImportMapForPackage } from "@jsenv/node-module-import-map"
import { jsenvBundlingDirectoryUrl } from "../../jsenvBundlingDirectoryUrl.js"
import {
  fileUrlToRelativePath,
  hasScheme,
  fileUrlToPath,
  pathToFileUrl,
  resolveFileUrl,
  resolveDirectoryUrl,
} from "../../urlHelpers.js"
import { writeSourceMappingURL } from "../../source-mapping-url.js"
import { generateBabelPluginMapOption } from "../../generateBabelPluginMapOption/generateBabelPluginMapOption.js"
import { fetchUrl } from "./fetchUrl.js"
import { fetchSourcemap } from "./fetchSourcemap.js"
import { validateResponseStatusIsOk } from "./validateResponseStatusIsOk.js"

const {
  readProjectImportMap,
  babelHelperMap,
  generateBabelHelper,
  cleanCompileCacheDirectoryIfObsolete,
  getOrGenerateCompiledFile,
  transformJs,
  transformResultToCompilationResult,
  compilationResultToTransformResult,
  findAsyncPluginNameInBabelPluginMap,
} = import.meta.require("@jsenv/core")
const { minify: minifyCode } = import.meta.require("terser")

export const createJsenvRollupPlugin = async ({
  cancellationToken,
  projectDirectoryUrl,
  bundleDirectoryUrl,
  importMapFileRelativePath,
  importMapForBundle,
  importDefaultExtension,
  importReplaceMap,
  importFallbackMap,
  bundleCache,
  bundleCacheDirectoryRelativePath,
  babelPluginMap,
  babelPluginRequiredNameArray,
  convertMap,
  minify,
  format,
  detectAndTransformIfNeededAsyncInsertedByRollup = format === "global",
  entryPointMap,
  logger,
}) => {
  const arrayOfUrlToSkipTransform = []
  const arrayOfAbstractUrl = []
  const moduleContentMap = {}

  const bundleConstantAbstractSpecifier = "/.jsenv/BUNDLE_CONSTANTS.js"

  babelPluginMap = generateBabelPluginMapOption({
    format,
    babelPluginMap,
    babelPluginRequiredNameArray,
  })

  importFallbackMap = {
    // importMap is optionnal
    "/importMap.json": () => `{}`,
    ...importFallbackMap,
  }
  importFallbackMap = resolveSpecifierMap(importFallbackMap, projectDirectoryUrl)

  const chunkId = `${Object.keys(entryPointMap)[0]}.js`
  importReplaceMap = {
    [bundleConstantAbstractSpecifier]: () => `export const chunkId = ${JSON.stringify(chunkId)}`,
    ...importReplaceMap,
  }
  Object.keys(babelHelperMap).forEach((babelHelperName) => {
    const babelHelperPath = babelHelperMap[babelHelperName]
    if (babelHelperPath.startsWith("@jsenv/core")) {
      // nothing to do
    } else {
      importReplaceMap[babelHelperPath] = () => generateBabelHelper(babelHelperName)
    }
  })
  importReplaceMap = resolveSpecifierMap(importReplaceMap, projectDirectoryUrl)

  const importMapForProject = await readProjectImportMap({
    logger,
    projectDirectoryUrl,
    jsenvProjectDirectoryUrl: jsenvBundlingDirectoryUrl,
    importMapFileRelativePath,
  })
  const importMap = normalizeImportMap(
    [
      await generateImportMapForPackage({
        projectDirectoryPath: fileUrlToPath(jsenvBundlingDirectoryUrl),
        rootProjectDirectoryPath: fileUrlToPath(projectDirectoryUrl),
        logger,
      }),
      ...(importMapFileRelativePath === "./importMap.json"
        ? []
        : [
            {
              imports: {
                "/importMap.json": importMapFileRelativePath,
              },
            },
          ]),
      importMapForProject,
      importMapForBundle,
    ].reduce((previous, current) => composeTwoImportMaps(previous, current), {}),
    projectDirectoryUrl,
  )

  const bundleCacheDirectoryUrl = resolveDirectoryUrl(
    bundleCacheDirectoryRelativePath,
    projectDirectoryUrl,
  )
  if (bundleCache) {
    const cacheMeta = {
      babelPluginRequiredNameArray,
      convertMap,

      // importMap,
      // importMap influences how the files are resolved
      // but not how they are compiled and cached

      // format,
      // not needed because derived from bundleIntoRelativePath

      // babelPluginMap,
      // not needed because derived from babelPluginRequiredNameArray

      // minify,
      // not needed, the bundle itself is not cached
      // and only the bundle is minified
    }
    await cleanCompileCacheDirectoryIfObsolete({
      cacheDirectoryUrl: bundleCacheDirectoryUrl,
      cacheMeta,
      cleanCallback: (cacheFolderPath) => {
        logger.warn(`remove cache folder ${cacheFolderPath}`)
      },
    })
  }

  const jsenvRollupPlugin = {
    name: "jsenv",

    resolveId: (specifier, importer = projectDirectoryUrl) => {
      if (!hasScheme(importer)) {
        importer = pathToFileUrl(importer)
      }

      const importUrl = resolveImport({
        specifier,
        importer,
        importMap,
        defaultExtension: importDefaultExtension,
      })

      // logger.debug(`resolve ${specifier} to ${importUrl}`)

      if (importUrl.startsWith("file://")) {
        // TODO: open an issue rollup side
        // to explain the issue here
        // which is that if id is not an os path
        // sourcemap will be broken
        // (likely because they apply node.js path.resolve)
        // they could either use an other resolution system
        // or provide the ability to change how it's resolved
        // so that the sourcemap.sources does not get broken

        // rollup works with operating system path
        // return os path when possible
        // to ensure we can predict sourcemap.sources returned by rollup
        const importPath = fileUrlToPath(importUrl)
        if (importUrl in importReplaceMap) {
          return importPath
        }
        // file presence is optionnal
        if (importUrl in importFallbackMap) {
          return importPath
        }

        let stats
        try {
          stats = statSync(importPath)
        } catch (e) {
          if (e.code === "ENOENT") {
            throw new Error(`import file not found.
--- path ---
${importPath}
--- specifier ---
${specifier}
--- importer ---
${importer}`)
          }
          throw e
        }

        if (!stats.isFile()) {
          throw new Error(`unexpected import file stats.
--- path ---
${importPath}
--- found ---
${stats.isDirectory()} ? 'directory' : 'not-a-file'
--- specifier ---
${specifier}
--- importer ---
${importer}`)
        }

        return importPath
      }

      return importUrl
    },

    // https://rollupjs.org/guide/en#resolvedynamicimport
    // resolveDynamicImport: (specifier, importer) => {

    // },

    load: async (rollupId) => {
      const url = rollupIdToUrl(rollupId)
      logger.debug(`loading ${url}`)
      const { contentRaw, content, map } = await loadModule(url)

      moduleContentMap[url] = {
        content,
        contentRaw,
      }

      return { code: content, map }
    },

    // resolveImportMeta: () => {}

    transform: async (moduleContent, rollupId) => {
      const url = rollupIdToUrl(rollupId)

      if (arrayOfUrlToSkipTransform.includes(url)) {
        logger.debug(`skip transform for ${url}`)
        return null
      }

      const transform = async () => {
        const { code, map } = await transformJs({
          projectDirectoryUrl,
          code: moduleContent,
          url,
          babelPluginMap,
          convertMap,
          // false, rollup will take care to transform module into whatever format
          transformModuleIntoSystemFormat: false,
        })
        return { code, map }
      }

      if (!bundleCache || !url.startsWith(projectDirectoryUrl)) {
        return transform()
      }

      const sourceRelativePath = fileUrlToRelativePath(url, bundleCacheDirectoryUrl)

      const { compileResult } = await getOrGenerateCompiledFile({
        projectDirectoryUrl,
        cacheDirectoryUrl: bundleCacheDirectoryUrl,
        sourceRelativePath,
        compile: async () => {
          const transformResult = await transform()
          return transformResultToCompilationResult(transformResult, {
            source: moduleContent,
            sourceUrl: url,
            projectDirectoryUrl,
          })
        },
      })

      return compilationResultToTransformResult(compileResult)
    },

    outputOptions: (options) => {
      // we want something like ../../../../file.js to become /file.js
      // and also rollup does not expects to have http dependency in the mix
      // so we fix them too (this one is really not mandatory)
      options.sourcemapPathTransform = (relativeSpecifier) => {
        const chunkFileUrl = resolveFileUrl(`./${chunkId}`, bundleDirectoryUrl)
        const url = resolveImport({
          specifier: relativeSpecifier,
          importer: chunkFileUrl,
        })

        if (url.startsWith(projectDirectoryUrl)) {
          // relativise project dependencies
          const relativePath = fileUrlToRelativePath(url, projectDirectoryUrl)
          const originRelativePath = relativePath.slice(1)

          if (originRelativePath.startsWith("/http:/")) {
            return `http://${originRelativePath.slice(`/http:/`.length)}`
          }
          if (originRelativePath.startsWith("/https:/")) {
            return `https://${originRelativePath.slice(`/http:/`.length)}`
          }
          return originRelativePath
        }
        return url
      }
      return options
    },

    renderChunk: (source) => {
      if (!minify) return null

      // https://github.com/terser-js/terser#minify-options
      const minifyOptions = format === "global" ? { toplevel: false } : { toplevel: true }
      const result = minifyCode(source, {
        sourceMap: true,
        ...minifyOptions,
      })
      if (result.error) {
        throw result.error
      } else {
        return result
      }
    },

    writeBundle: async (bundle) => {
      if (detectAndTransformIfNeededAsyncInsertedByRollup) {
        await transformAsyncInsertedByRollup({
          projectDirectoryUrl,
          bundleDirectoryUrl,
          babelPluginMap,
          bundle,
        })
      }

      Object.keys(bundle).forEach((bundleFilename) => {
        logger.info(`-> ${bundleDirectoryUrl}${bundleFilename}`)
      })
    },
  }

  const markUrlAsAbstract = (url) => {
    if (!arrayOfAbstractUrl.includes(url)) {
      arrayOfAbstractUrl.push(url)
    }
  }

  const loadModule = async (moduleUrl) => {
    const { contentType, content, map } = await getModule(moduleUrl)

    if (contentType === "application/javascript") {
      return {
        contentRaw: content,
        content,
        map: await fetchSourcemap({
          cancellationToken,
          logger,
          moduleUrl,
          moduleContent: content,
        }),
      }
    }

    if (contentType === "application/json") {
      return {
        contentRaw: content,
        content: `export default ${content}`,
        map,
      }
    }

    if (contentType.startsWith("text/")) {
      return {
        contentRaw: content,
        content: `export default ${JSON.stringify(content)}`,
        map,
      }
    }

    logger.warn(`unexpected content-type for module.
--- content-type ---
${contentType}
--- expected content-types ---
"application/javascript"
"application/json"
"text/*"
--- module url ---
${moduleUrl}`)

    return {
      contentRaw: content,
      // fallback to text
      content: `export default ${JSON.stringify(content)}`,
      map,
    }
  }

  const getModule = async (moduleUrl) => {
    if (moduleUrl in importReplaceMap) {
      const generateSourceForImport = importReplaceMap[moduleUrl]
      if (typeof generateSourceForImport !== "function") {
        throw new Error(
          `specifierAbstractMap values must be functions, found ${generateSourceForImport} for ${moduleUrl}`,
        )
      }

      return generateAbstractModuleForImport(moduleUrl, generateSourceForImport)
    }

    const response = await fetchUrl(moduleUrl, { cancellationToken })
    const okValidation = validateResponseStatusIsOk(response)

    if (!okValidation.valid) {
      if (response.status === 404 && moduleUrl in importFallbackMap) {
        return generateAbstractModuleForImport(moduleUrl, importFallbackMap[moduleUrl])
      }
      throw new Error(okValidation.message)
    }

    return {
      contentType: response.headers["content-type"],
      content: response.body,
    }
  }

  const generateAbstractModuleForImport = async (url, generateSource) => {
    markUrlAsAbstract(url)

    const returnValue = await generateSource()
    const result = typeof returnValue === "string" ? { code: returnValue } : returnValue

    const { skipTransform, code, map } = result

    if (skipTransform) {
      arrayOfUrlToSkipTransform.push(url)
    }

    return {
      contentType: ressourceToContentType(hrefToPathname(url), defaultContentTypeMap),
      content: code,
      map,
    }
  }

  return {
    jsenvRollupPlugin,
    getExtraInfo: () => {
      return {
        arrayOfAbstractUrl,
        moduleContentMap,
      }
    },
  }
}

export const rollupIdToUrl = (rollupId) => {
  if (hasScheme(rollupId)) {
    return rollupId
  }

  return pathToFileUrl(rollupId)
}

const resolveSpecifierMap = (specifierMap, projectDirectoryUrl) => {
  const specifierMapResolved = {}
  Object.keys(specifierMap).forEach((specifier) => {
    const specifierResolved = resolveFileUrl(specifier, projectDirectoryUrl)
    specifierMapResolved[specifierResolved] = specifierMap[specifier]
  })
  return specifierMapResolved
}

const transformAsyncInsertedByRollup = async ({
  projectDirectoryUrl,
  bundleDirectoryUrl,
  babelPluginMap,
  bundle,
}) => {
  const asyncPluginName = findAsyncPluginNameInBabelPluginMap(babelPluginMap)

  if (!asyncPluginName) return

  // we have to do this because rollup ads
  // an async wrapper function without transpiling it
  // if your bundle contains a dynamic import
  await Promise.all(
    Object.keys(bundle).map(async (bundleFilename) => {
      const bundleInfo = bundle[bundleFilename]

      const { code, map } = await transformJs({
        projectDirectoryUrl,
        code: bundleInfo.code,
        url: pathToFileUrl(bundleFilename),
        map: bundleInfo.map,
        babelPluginMap: { [asyncPluginName]: babelPluginMap[asyncPluginName] },
        transformModuleIntoSystemFormat: false, // already done by rollup
        transformGenerator: false, // already done
      })

      const bundleFileUrl = resolveFileUrl(bundleFilename, bundleDirectoryUrl)

      await Promise.all([
        fileWrite(
          fileUrlToPath(bundleFileUrl),
          writeSourceMappingURL(code, `./${bundleFilename}.map`),
        ),
        fileWrite(fileUrlToPath(`${bundleFileUrl}.map`), JSON.stringify(map)),
      ])
    }),
  )
}
